// FTR F19 — reusable icon button with hover/active states + tooltip

import {useState, useCallback, useRef, type CSSProperties, type ReactNode, type MouseEvent} from 'react'

// --

type IconButtonProps = {
	icon: ReactNode
	title: string
	onClick: (e: MouseEvent<HTMLButtonElement>) => void
	variant?: 'ghost' | 'outline'
	size?: 'sm' | 'md'
	disabled?: boolean
}

const base: CSSProperties = {
	border: 'none',
	cursor: 'pointer',
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	borderRadius: 4,
	transition: 'background 0.1s, color 0.1s, border-color 0.1s',
	position: 'relative',
}

const variants = {
	ghost: {
		rest: {background: 'none', color: '#666'},
		hover: {background: '#f0f0f0', color: '#333'},
		active: {background: '#e0e0e0', color: '#111'},
	},
	outline: {
		rest: {background: '#fff', color: '#444', border: '1px solid #ddd', borderRadius: 6},
		hover: {background: '#f5f5f5', color: '#222', borderColor: '#bbb'},
		active: {background: '#eee', color: '#111', borderColor: '#999'},
	},
} as const

const sizes = {
	sm: {padding: 3},
	md: {padding: '5px 8px'},
} as const

export function IconButton({icon, title, onClick, variant = 'ghost', size = 'sm', disabled}: IconButtonProps) {
	const [hover, setHover] = useState(false)
	const [active, setActive] = useState(false)
	const [tooltip, setTooltip] = useState<{x: number; y: number} | null>(null)
	const timerRef = useRef<ReturnType<typeof setTimeout>>()
	const btnRef = useRef<HTMLButtonElement>(null)

	const v = variants[variant]
	const s = sizes[size]

	const style: CSSProperties = {
		...base,
		...s,
		...v.rest,
		...(hover && !disabled ? v.hover : {}),
		...(active && !disabled ? v.active : {}),
		...(disabled ? {opacity: 0.4, cursor: 'default'} : {}),
	}

	const handleEnter = useCallback(() => {
		setHover(true)
		timerRef.current = setTimeout(() => {
			if (btnRef.current) {
				const r = btnRef.current.getBoundingClientRect()
				setTooltip({x: r.left + r.width / 2, y: r.bottom + 4})
			}
		}, 400)
	}, [])

	const handleLeave = useCallback(() => {
		setHover(false)
		setActive(false)
		setTooltip(null)
		if (timerRef.current) clearTimeout(timerRef.current)
	}, [])

	return (
		<>
			<button
				ref={btnRef}
				style={style}
				title={title}
				onClick={disabled ? undefined : onClick}
				onMouseEnter={handleEnter}
				onMouseLeave={handleLeave}
				onMouseDown={() => setActive(true)}
				onMouseUp={() => setActive(false)}
			>
				{icon}
			</button>
			{tooltip && (
				<div style={{
					position: 'fixed',
					left: tooltip.x,
					top: tooltip.y,
					transform: 'translateX(-50%)',
					background: '#333',
					color: '#fff',
					fontSize: 11,
					padding: '3px 8px',
					borderRadius: 4,
					whiteSpace: 'nowrap',
					pointerEvents: 'none',
					zIndex: 2000,
				}}>
					{title}
				</div>
			)}
		</>
	)
}
